import type { Ref } from 'vue';
import { ref, computed, watch } from 'vue';
import { isAxiosError } from 'axios';
import { membersApi } from '@/entities/Branch';
import { useDepartmentOptions } from './useDepartmentOptions';
import { useFunctionalActivityOptions } from './useFunctionalActivityOptions';
import { usePositionOptionsCache, type PositionDepartmentOption } from './usePositionOptionsCache';
import { useCustomToast } from '@/shared/lib';

export type Option = PositionDepartmentOption;

export interface PositionRow {
  _key: number;
  departmentId: string | number;
  positionId: string | number;
  orphanPositionName?: string;
}

export interface FunctionalActivityRow {
  _key: number;
  functionalActivityId: string | number;
}

let nextPositionRowKey = 0;
let nextFaRowKey = 0;

export function createPositionRow(
  partial: {
    departmentId?: string | number;
    positionId?: string | number;
    orphanPositionName?: string;
  } = {}
): PositionRow {
  return {
    _key: ++nextPositionRowKey,
    departmentId: partial.departmentId ?? '',
    positionId: partial.positionId ?? '',
    orphanPositionName: partial.orphanPositionName,
  };
}

export function createFunctionalActivityRow(
  partial: { functionalActivityId?: string | number } = {}
): FunctionalActivityRow {
  return {
    _key: ++nextFaRowKey,
    functionalActivityId: partial.functionalActivityId ?? '',
  };
}

export function useEditMemberForm(memberId: Ref<number>) {
  const toast = useCustomToast();
  const memberName = ref('');
  const positions = ref<PositionRow[]>([createPositionRow()]);
  const functionalActivities = ref<FunctionalActivityRow[]>([createFunctionalActivityRow()]);
  const hasFunctionalActivity = ref(false);
  const savedFunctionalActivities = ref<FunctionalActivityRow[]>([]);
  const isLoadingMember = ref(true);
  const isSaving = ref(false);
  const loadError = ref(false);

  const {
    positionOptionsCache,
    loadPositionsForDepartment,
    isDepartmentLoadingPositions,
    getPositionOptions: getCachedPositionOptions,
  } = usePositionOptionsCache();
  const catalogsLoaded = ref(false);

  const {
    departmentOptions,
    isLoading: isLoadingDepartments,
    loadDepartments,
  } = useDepartmentOptions();

  const {
    functionalActivityOptions,
    isLoading: isLoadingActivities,
    loadFunctionalActivities,
  } = useFunctionalActivityOptions();

  const transformedDepartmentOptions = computed(() =>
    departmentOptions.value.map((opt) => ({
      id: opt.value,
      name: opt.label,
    }))
  );

  const getPositionOptions = (departmentId: string | number, currentPositionId?: string | number): Option[] => {
    const all = getCachedPositionOptions(departmentId);
    const currentId = currentPositionId ? Number(currentPositionId) : null;
    const used = new Set(
      positions.value
        .filter((p) => p.positionId !== '' && Number(p.positionId) !== currentId)
        .map((p) => Number(p.positionId))
    );
    const options = all.filter((opt) => !used.has(Number(opt.id)));

    if (currentId !== null && !options.some((opt) => Number(opt.id) === currentId)) {
      const orphanRow = positions.value.find(
        (p) => Number(p.positionId) === currentId && p.orphanPositionName
      );
      if (orphanRow?.orphanPositionName) {
        options.unshift({ id: currentId, name: orphanRow.orphanPositionName });
      }
    }

    return options;
  };

  const getFilteredFaOptions = (index: number): Option[] => {
    const currentId = functionalActivities.value[index]?.functionalActivityId;
    const currentNum = currentId !== '' ? Number(currentId) : null;
    const used = new Set(
      functionalActivities.value
        .filter((a, i) => i !== index && a.functionalActivityId !== '')
        .map((a) => Number(a.functionalActivityId))
    );
    return functionalActivityOptions.value.filter((opt) => {
      const id = Number(opt.id);
      return id === currentNum || !used.has(id);
    });
  };

  const updatePositionDepartment = (index: number, departmentId: string | number) => {
    const next = [...positions.value];
    next[index] = { ...next[index], departmentId, positionId: '', orphanPositionName: undefined };
    positions.value = next;
    if (departmentId) {
      void loadPositionsForDepartment(Number(departmentId));
    }
  };

  const updatePositionId = (index: number, positionId: string | number) => {
    const next = [...positions.value];
    next[index] = { ...next[index], positionId };
    positions.value = next;
  };

  const addPosition = () => {
    positions.value = [...positions.value, createPositionRow()];
  };

  const removePosition = (index: number) => {
    positions.value = positions.value.filter((_, i) => i !== index);
  };

  const updateFunctionalActivity = (index: number, functionalActivityId: string | number) => {
    const next = [...functionalActivities.value];
    next[index] = { ...next[index], functionalActivityId };
    functionalActivities.value = next;
  };

  const addFunctionalActivity = () => {
    functionalActivities.value = [...functionalActivities.value, createFunctionalActivityRow()];
  };

  const removeFunctionalActivity = (index: number) => {
    functionalActivities.value = functionalActivities.value.filter((_, i) => i !== index);
  };

  const toggleFunctionalActivity = (value: boolean) => {
    hasFunctionalActivity.value = value;
    if (value) {
      if (savedFunctionalActivities.value.length > 0) {
        functionalActivities.value = [...savedFunctionalActivities.value];
        savedFunctionalActivities.value = [];
      } else {
        functionalActivities.value = [createFunctionalActivityRow()];
      }
    } else {
      savedFunctionalActivities.value = functionalActivities.value.filter(
        (a) => a.functionalActivityId !== ''
      );
      functionalActivities.value = [createFunctionalActivityRow()];
    }
  };

  const hydrateFromMember = async () => {
    const id = memberId.value;
    if (!id || id <= 0) {
      savedFunctionalActivities.value = [];
      isLoadingMember.value = false;
      loadError.value = false;
      return;
    }
    isLoadingMember.value = true;
    loadError.value = false;
    try {
      const member = await membersApi.getById(id);
      memberName.value = member.user.registrationNumber
        ? `${member.user.name} — ${member.user.registrationNumber}`
        : member.user.name;
      const posRows: PositionRow[] =
        member.positions.length > 0
          ? member.positions.map((p) =>
              createPositionRow({
                departmentId: p.department?.id ?? '',
                positionId: p.id,
                orphanPositionName: p.department ? undefined : p.name,
              })
            )
          : [createPositionRow()];
      positions.value = posRows;
      const departmentIdsToLoad = [
        ...new Set(posRows.map((row) => row.departmentId).filter(Boolean)),
      ];
      await Promise.all(
        departmentIdsToLoad.map((depId) => loadPositionsForDepartment(Number(depId)))
      );
      savedFunctionalActivities.value = [];
      hasFunctionalActivity.value = member.functionalActivities.length > 0;
      const faRows: FunctionalActivityRow[] =
        member.functionalActivities.length > 0
          ? member.functionalActivities.map((fa) =>
              createFunctionalActivityRow({ functionalActivityId: String(fa.id) })
            )
          : [createFunctionalActivityRow()];
      functionalActivities.value = faRows;
    } catch {
      loadError.value = true;
      toast.showErrorToast({
        title: 'Não foi possível carregar o membro.',
        type: 'error',
      });
    } finally {
      isLoadingMember.value = false;
    }
  };

  watch(
    memberId,
    async () => {
      if (!catalogsLoaded.value) {
        await Promise.all([loadDepartments(), loadFunctionalActivities()]);
        catalogsLoaded.value = true;
      }
      await hydrateFromMember();
    },
    { immediate: true }
  );

  const canAddPosition = computed(() => {
    const last = positions.value[positions.value.length - 1];
    return last?.positionId !== '';
  });

  const canAddFunctionalActivity = computed(() => {
    const last = functionalActivities.value[functionalActivities.value.length - 1];
    if (!last || last.functionalActivityId === '') return false;
    const usedCount = functionalActivities.value.filter((a) => a.functionalActivityId !== '').length;
    return usedCount < functionalActivityOptions.value.length;
  });

  const validationMessage = computed(() => '');


  const isFormValid = computed(() => validationMessage.value === '');

  const handleSave = async (): Promise<boolean> => {
    if (!isFormValid.value || isSaving.value || loadError.value) return false;
    const id = memberId.value;
    const positionIds = [
      ...new Set(
        positions.value
          .filter((p) => p.positionId !== '')
          .map((p) => Number(p.positionId))
      ),
    ];
    const functionalActivityIds = [
      ...new Set(
        functionalActivities.value
          .filter((a) => a.functionalActivityId !== '')
          .map((a) => Number(a.functionalActivityId))
      ),
    ];
    isSaving.value = true;
    try {
      await membersApi.update(id, {
        positionIds,
        functionalActivityIds,
      });
      toast.showSuccessToast({
        title: 'Edição salva com sucesso.',
        type: 'success',
      });
      return true;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.data?.message) {
        toast.showErrorToast({
          title: String(error.response.data.message),
          type: 'error',
        });
      } else {
        toast.showErrorToast({
          title: 'Erro ao salvar edições. Tente novamente.',
          type: 'error',
        });
      }
      return false;
    } finally {
      isSaving.value = false;
    }
  };

  return {
    memberName,
    positions,
    functionalActivities,
    hasFunctionalActivity,
    isLoadingMember,
    isSaving,
    loadError,
    positionOptionsCache,
    transformedDepartmentOptions,
    functionalActivityOptions,
    isLoadingDepartments,
    isLoadingActivities,
    getPositionOptions,
    getFilteredFaOptions,
    isDepartmentLoadingPositions,
    loadPositionsForDepartment,
    updatePositionDepartment,
    updatePositionId,
    addPosition,
    removePosition,
    updateFunctionalActivity,
    addFunctionalActivity,
    removeFunctionalActivity,
    toggleFunctionalActivity,
    hydrateFromMember,
    canAddPosition,
    canAddFunctionalActivity,
    validationMessage,
    isFormValid,
    handleSave,
  };
}
